import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { STATE_MACHINE, useMergeContext } from '../../../store/mergeContext'
import { Box, Stack } from '@mui/material'
import { Button, Typography, useTheme } from '@mui/material'
import PossibleMatchGrid from '../components/PossibleMatchGrid'
import { Trans, t } from '@lingui/macro'
import { NewApiBaseUrl } from '../../../consts/new-api-base-url'
import useHttpApiNew from '../../../hooks/useHttpApiNew'
import SvgCustomIcon from '../../../components/icons/SvgCustomIcon'
import {
  BASIC_ORG,
  BASIC_TO_KIND,
  BASIC_TO_ROLE,
  enrichCrashDetail,
  getMatchTargetsForSource,
  getOrgLabelByBasic,
} from '../merge-constants'
import { buildMatchGroups } from '../match-grid'
import { CRASH_ID_KEY } from '../utils'
import CrashMatchGroupList from '../components/CrashMatchGroupList'

export const MatchTable = () => {
  const {
    selectedCrash,
    setMatches,
    setCurrentState,
    setPolicePhotos,
    sourceBasicOrganization,
    mergeCapabilities,
    hasUnresolvedCollisions,
    setCrashForRole,
  } = useMergeContext()
  const myLocation = useLocation()
  const token = myLocation.state?.token || null
  const [crashDetail, setCrashDetail] = useState([])
  const [searchTargets, setSearchTargets] = useState(null)
  const init = useMemo(
    () => myLocation.state?.init || {},
    [myLocation.state?.init]
  )
  const theme = useTheme()
  const { request } = useHttpApiNew()

  const organizations = useMemo(
    () => myLocation.state?.init?.organizations || [],
    [myLocation.state?.init?.organizations]
  )

  const anchorRole = BASIC_TO_ROLE[sourceBasicOrganization] ?? 'police'
  const configuration = init?.configuration

  const matchTargetBasics = useMemo(() => {
    if (Array.isArray(searchTargets) && searchTargets.length > 0) {
      return searchTargets.filter((b) => b !== sourceBasicOrganization)
    }
    return getMatchTargetsForSource(sourceBasicOrganization)
  }, [searchTargets, sourceBasicOrganization])

  const selectedCrashTitle = useMemo(() => {
    const label = getOrgLabelByBasic(sourceBasicOrganization, organizations, {
      police: t`Police`,
      hospital: t`Hospital`,
      insurance: t`Insurance`,
    })
    return t`Selected ${label} crash`
  }, [sourceBasicOrganization, organizations])

  const { canMerge, canValidateOnly, selectedRoleCount } = mergeCapabilities

  const DISABLE_VALIDATE_CRASH = useMemo(() => {
    if (sourceBasicOrganization !== BASIC_ORG.POLICE) return true
    return !canValidateOnly
  }, [sourceBasicOrganization, canValidateOnly])

  const DISABLE_MERGE_CRASHES = useMemo(
    () => !canMerge || hasUnresolvedCollisions,
    [canMerge, hasUnresolvedCollisions],
  )

  const mergeDisabledHint = useMemo(() => {
    if (hasUnresolvedCollisions) {
      return t`Resolve person pairing conflicts before merging`
    }
    if (canMerge) return ''
    if (selectedRoleCount < 2) {
      return t`Select records from at least two organizations to merge`
    }
    return ''
  }, [canMerge, selectedRoleCount, hasUnresolvedCollisions])

  const selectedCrashGroups = useMemo(
    () => buildMatchGroups(crashDetail ?? [], configuration ?? {}, {
      organizationBasic: sourceBasicOrganization,
    }),
    [crashDetail, configuration, sourceBasicOrganization],
  )

  const getMatches = useCallback(async () => {
    const matchUrlReduced = new URL(`${NewApiBaseUrl}/match/${selectedCrash}`)
    matchUrlReduced.searchParams.set('format', 'table')
    const res = await request(matchUrlReduced, 'GET', null, { Authorization: `Bearer ${token}` })
    if (res.responseAnyError) return
    setMatches(res.responseData.matches)
    if (Array.isArray(res.responseData.search_targets)) {
      setSearchTargets(res.responseData.search_targets)
    } else if (res.responseData.source_basic_organization != null) {
      setSearchTargets(getMatchTargetsForSource(res.responseData.source_basic_organization))
    } else {
      setSearchTargets(null)
    }
  }, [token, selectedCrash, setMatches, request])

  const getCrashes = useCallback(async () => {
    const crashUrl = new URL(`${NewApiBaseUrl}/crash/${selectedCrash}`)
    const res = await request(crashUrl, 'GET', null, { Authorization: `Bearer ${token}` })
    if (res.responseAnyError) return

    setCrashDetail(res.responseData.crashes ?? [])
    const crashes = res.responseData?.crashes ?? []
    const anchorCrash =
      crashes.find((crash) => crash?.crash?.[CRASH_ID_KEY] === selectedCrash) ??
      crashes[0]
    if (anchorCrash) {
      setCrashForRole(anchorRole, enrichCrashDetail(anchorCrash))
    }
    const images = anchorCrash?.images ?? anchorCrash?.crash?.images
    if (anchorRole === 'police' && images?.length) {
      setPolicePhotos(images)
    }
  }, [token, selectedCrash, anchorRole, setPolicePhotos, setCrashForRole, request])

  useEffect(() => {
    if (selectedCrash) {
      getCrashes()
    }
  }, [selectedCrash, getCrashes])

  useEffect(() => {
    if (selectedCrash) {
      getMatches()
    }
  }, [selectedCrash, getMatches])

  return (
    <Stack gap={6}>
      <Box>
        <Typography
          sx={{
            width: '100%',
            mt: 2,
            mb: 2,
            color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
            fontSize: 20,
            fontWeight: 'bold',
          }}
          variant="h5"
        >
          {selectedCrashTitle}
        </Typography>
        <CrashMatchGroupList
          groups={selectedCrashGroups}
          configuration={configuration}
          organizationBasic={sourceBasicOrganization}
          defaultExpandAll
        />
      </Box>

      {matchTargetBasics.map((targetBasic) => (
        <Box key={targetBasic}>
          <PossibleMatchGrid
            type={BASIC_TO_KIND[targetBasic]}
            targetBasic={targetBasic}
          />
        </Box>
      ))}

      <Box alignSelf="end" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }} paddingY={3}>
        {mergeDisabledHint ? (
          <Typography variant="body2" color="warning.main">
            {mergeDisabledHint}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            sx={{ background: theme.palette.newColors.pinkGradient, color: 'white', display: 'flex', gap: 1 }}
            onClick={() => {
              setCurrentState(STATE_MACHINE.MERGE_PAGE)
            }}
            disabled={DISABLE_MERGE_CRASHES}
          >
            <SvgCustomIcon name={'merge'} color={'white'} />
            <Trans>Merge selected crashes</Trans>
          </Button>
          <Button
            sx={{ background: theme.palette.newColors.pinkGradient, color: 'white', display: 'flex', gap: 1 }}
            onClick={() => {
              setCurrentState(STATE_MACHINE.MERGE_PAGE)
            }}
            disabled={DISABLE_VALIDATE_CRASH}
            variant="outlined"
          >
            <SvgCustomIcon name={'check'} color={'white'} />
            <Trans>Validate crash</Trans>
          </Button>
        </Box>
      </Box>
    </Stack>
  )
}
